/**
 * SolicitudDocViewer.jsx
 * Visor de documentos del expediente de solicitudes,
 * replicando el diseño de TramiteDetalle.jsx
 */
import React from 'react';
import { Eye, Download, FileText } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || 'https://cbrsancarlos.cl';

/**
 * Devuelve el tipo de archivo en base a la extensión del nombre.
 */
function getTipo(nombre = '') {
  const ext = nombre.split('.').pop().toUpperCase();
  const tipos = { PDF: 'PDF', PNG: 'Imagen', JPG: 'Imagen', JPEG: 'Imagen',
    WEBP: 'Imagen', DOC: 'Word', DOCX: 'Word', XLS: 'Excel', XLSX: 'Excel' };
  return tipos[ext] || ext || 'Archivo';
}

/**
 * Item individual de documento.
 * Genera URLs hacia /api/v1/solicitudes/documentos/{token} o /api/v1/archivero/documentos/{token}
 */
function DocItem({ doc, isArchivero }) {
  const tipo = getTipo(doc.nombre_original);
  const prefix = isArchivero ? 'archivero' : 'solicitudes';
  const urlVer       = `${API}/api/v1/${prefix}/documentos/${doc.token_descarga}`.replace(/([^:]\/)\/+/g, "$1");
  const urlDescargar = `${API}/api/v1/${prefix}/documentos/${doc.token_descarga}/descargar`.replace(/([^:]\/)\/+/g, "$1");

  return (
    <div
      className="glass-card"
      style={{
        padding: '0.75rem 1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 10,
        transition: 'background 0.2s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
    >
      {/* Info del archivo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: 'var(--midnight, #0f1329)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          📄
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: '0.87rem', fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            color: 'var(--text)',
          }}>
            {doc.nombre_original}
          </div>
          <div style={{ fontSize: '0.7rem', color: 'var(--gray-500, #6b7280)', marginTop: 2 }}>
            {tipo}
            {doc.created_at && (
              <span style={{ marginLeft: 6 }}>
                · {new Date(doc.created_at).toLocaleDateString('es-CL')}
              </span>
            )}
            {doc.subido_por?.name && (
              <span style={{ marginLeft: 6 }}>· {doc.subido_por.name}</span>
            )}
          </div>
        </div>
      </div>

      {/* Acciones */}
      <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 8 }}>
        <a
          href={urlVer}
          target="_blank"
          rel="noreferrer"
          className="icon-btn"
          title="Ver documento"
          style={{ padding: '6px 10px' }}
        >
          <Eye size={16} />
        </a>
        <a
          href={urlDescargar}
          target="_blank"
          rel="noreferrer"
          className="icon-btn"
          title="Descargar"
          style={{ padding: '6px 10px' }}
        >
          <Download size={16} />
        </a>
      </div>
    </div>
  );
}

/**
 * Panel completo del visor.
 * Acepta:
 *  - docs: array de SolicitudDocumento
 *  - titulo: string (default "Documentos del Expediente")
 *  - emptyText: string (mensaje cuando no hay docs)
 */
export default function SolicitudDocViewer({
  docs = [],
  titulo = 'Documentos del Expediente',
  emptyText = 'No hay documentos adjuntos.',
  isArchivero = false,
}) {
  return (
    <div>
      <h3 style={{
        fontSize: '1.05rem', marginBottom: '1rem',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <FileText size={18} style={{ color: 'var(--copper)' }} />
        {titulo}
        {docs.length > 0 && (
          <span style={{
            fontSize: '0.72rem', background: 'var(--copper)', color: '#fff',
            borderRadius: 12, padding: '2px 8px', marginLeft: 4, fontWeight: 700,
          }}>
            {docs.length}
          </span>
        )}
      </h3>

      {docs.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {docs.map(doc => <DocItem key={doc.id} doc={doc} isArchivero={isArchivero} />)}
        </div>
      ) : (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          {emptyText}
        </p>
      )}
    </div>
  );
}
